/* eslint-disable no-unused-vars */
import type { Auth } from './auth'
import { fetchRetry } from './fetch-retry'
import { envBoolean } from '@/libs/utils/env'
import { FetchError } from '@/libs/errors'
import { parseUrl } from '@/libs/utils/path'
// import memoizee from 'memoizee'

type FetchOptions = {
  baseUrl?: string
  path: string
  body?: any
  method: string
  retry?: number
  reauthenticate?: boolean
  controller?: AbortController
  headers?: Record<string, string>
}

type FetchMethodOptions = Omit<FetchOptions, 'method'>

type HttpClientOptions = {
  serviceName: string
  baseUrl: string
  auth?: Auth
  cookie?: string
  authorization?: string
}

export type FetchResult = {
  response: () => Promise<Response>
  controller: AbortController
}

export class HttpClient {
  public readonly serviceName: string
  public readonly baseUrl: string
  private auth?: Auth
  private cookie?: string
  private authorization?: string

  // public readonly get = memoizee(this.getInternal.bind(this), { maxAge: 10 })

  constructor(options: HttpClientOptions) {
    if (!options.serviceName) {
      throw new Error(
        'Invalid serviceName, A valid serviceName is required to initialize the HttpClient',
      )
    }

    if (!options.baseUrl) {
      throw new Error('Invalid baseUrl, A valid baseUrl is required to initialize the HttpClient')
    }

    this.serviceName = options.serviceName
    this.baseUrl = options.baseUrl
    this.auth = options.auth
    this.cookie = options.cookie
    this.authorization = options.authorization
  }

  get(options: FetchMethodOptions): FetchResult {
    return this.fetch({
      ...options,
      method: 'GET',
    })
  }

  post(options: FetchMethodOptions): FetchResult {
    return this.fetch({
      ...options,
      method: 'POST',
    })
  }

  put(options: FetchMethodOptions): FetchResult {
    return this.fetch({
      ...options,
      method: 'PUT',
    })
  }

  patch(options: FetchMethodOptions): FetchResult {
    return this.fetch({
      ...options,
      method: 'PATCH',
    })
  }

  delete(options: FetchMethodOptions): FetchResult {
    return this.fetch({
      ...options,
      method: 'DELETE',
    })
  }

  private fetch(options: FetchOptions): FetchResult {
    const controller = options.controller || new AbortController()
    const {
      baseUrl = this.baseUrl,
      path,
      method,
      body,
      retry = 0,
      reauthenticate = false,
      headers = {},
    } = options
    const url = path.startsWith('http') ? path : parseUrl(`${baseUrl}/${path}`)
    headers['Content-Type'] = 'application/json; charset=utf-8'
    if (this.cookie) headers['Cookie'] = this.cookie
    if (this.authorization) headers['Authorization'] = this.authorization

    const requestInit: RequestInit = {
      method,
      headers,
      credentials: 'include',
      redirect: 'manual',
      body: undefined,
      signal: controller.signal,
    }

    if (body) {
      requestInit.body = JSON.stringify(body)
    }

    const responsePromise = this.fetchInternal(url, requestInit, retry, reauthenticate)
    return { response: () => responsePromise, controller }
  }
  private async fetchInternal(
    url: string,
    requestInit: RequestInit,
    retry: number,
    reauthenticate = false,
  ): Promise<Response> {
    const isAuthenticationEnabled = envBoolean('AUTHENTICATION_ENABLED', true)
    if (isAuthenticationEnabled && this.auth) {
      await this.auth.authenticate(requestInit, url, this.serviceName, reauthenticate)
    }
    try {
      let response = await fetchRetry(url, requestInit)
      const status = response.status
      if (isRedirect(status)) {
        let redirectUrl = response.headers.get('location')
        if (redirectUrl) {
          if (url.startsWith('https://')) {
            redirectUrl = redirectUrl?.replace('http://', 'https://')
          }
          console.info('Redirecting', {
            status,
            from: url,
            to: redirectUrl,
          })
          response = await fetchRetry(redirectUrl, requestInit)
        }
      }
      if (isAuthError(status) && retry < 3) {
        console.info(`Retring with new auth token, retry=${retry}, ${url}`)
        return this.fetchInternal(url, requestInit, retry + 1, true)
      }
      if (!response.ok) {
        const { status = '', statusText = '' } = response || {}
        let errorMessage = `HTTP error - ${url}.`
        if (status) errorMessage += ` Status: ${status}`
        if (statusText) errorMessage += ` Text: ${statusText}`
        const { ...opts } = requestInit
        const metadata = { url, opts, response }
        throw new FetchError(`${errorMessage}, retry=${retry}`, metadata)
      }
      return response
    } catch (error) {
      const message = (error as Error).message
      const { ...opts } = requestInit
      const metadata = (error as FetchError).metadata || {
        url,
        opts,
      }
      console.error(message, metadata)
      throw new FetchError(message, metadata)
    }
  }
}

const isAuthError = (status: number) => status === 401 || status === 403
const isRedirect = (status: number) => [301, 307, 308].includes(status)
